import React, { useCallback, useState } from 'react';
import { Skeleton, Spin } from 'antd';
import {
    SquaresFourIcon,
    CheckSquareIcon,
    HeartIcon,
} from '@phosphor-icons/react';
import type { SavedAIDesign, SelectedDesignForPricing } from '../../../../../store/oneButtonStore';
import {
    addModulesToRoofsByIds,
    removeGreyModulesAndSingleModuleRoofs,
    SetModuleGraph,
} from '../../Three-JS-Viewer/Controllers/ModuleControl';
import { restoreInitialDefaultLayout } from '../../Three-JS-Viewer/Controllers/DefaultLayoutControl';
import { formatLayoutNotSellableReason, isLayoutProductionOverAnnualUsage } from '../utils/layout';
import type { AllAiCombinationsLayoutCardDetails } from './DashboardAllAiDesignCombinationsModal';
import { DashboardAiCatalogListRow } from './DashboardAiCatalogListRow';
import { DashboardAiDesignsEpcModal } from './DashboardAiDesignsEpcModal';
import type { DashboardAiDesignEpcRow } from './DashboardAiDesignsEpcModal.types';
import type { DesignEpcCell, EpcDisplayPartner } from '../utils/pricing';
import DashboardModal from '../DashboardModal';
import { OBD_AI_DESIGNS_EPC_NESTED_MODAL_Z, OBD_DASHBOARD_MODAL_Z } from '../oneButtonOverlayZ';
import { resolveAltLayoutVariantSnapshot, catalogCardSnapshotForDisplay } from '../utils/catalogCardSnapshot';
import {
    filterAltLayoutDisplayVariants,
    positiveLayoutMetric,
    resolveAltLayoutVariantMetrics,
    resolveVariantsForCategory,
    type AltLayoutVariantRow,
} from '../utils/filterAltLayoutDisplayVariants';
import { useViewerStore } from '../../../../../store/store';
import type { AiDesignsDataSource } from './DashboardAiDesignsSourceBadge';

export type DashboardAltLayoutOptionsModalProps = {
    theme: 'dark' | 'light';
    /** When true, render catalog body only (no Ant Modal) for inline dashboard sub-panels */
    embedded?: boolean;
    /** Narrow viewer AI slide rail — single-column stack, no modal chrome */
    slideRail?: boolean;
    open: boolean;
    /** Full reset (backdrop / X): close modal and clear category + loading. */
    onDismiss: () => void;
    /** Close modal only (keep category state). */
    closeModal: () => void;
    altLayoutsCategory: string | null;
    altLayoutsLoading: boolean;
    designCountByCategory: Record<string, number>;
    aiCategoryLayoutVariants: Record<string, unknown[]>;
    /** Global portrait/landscape variants (when no category is selected). */
    globalLayoutVariants?: any[];
    roofCombinationsFromAi: any[];
    layoutProductionByKey: Record<string, number | undefined> | undefined;
    layoutModulesByKey: Record<string, number | undefined> | undefined;
    layoutMetricsByKey: Record<string, unknown> | undefined;
    activeModule: unknown;
    dashboardSystemSizeKw: number | null | undefined;
    savedAIDesigns: SavedAIDesign[];
    selectedDesignForPricing: SelectedDesignForPricing | null;
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    pricingSelectionLoading: boolean;
    aiLayoutVariantSnapshots: Record<string, string>;
    /** Fallback roof snapshots when variant capture missed (same as main AI catalogue cards). */
    layoutSnapshotsByKey?: Record<string, string | undefined>;
    selectedRoofCombinationKey?: string;
    annualUsageCapKwh: number | null | undefined;
    isLoadingSnapshots: boolean;
    handleUseThisLayout?: (scenarioIndex: number) => void;
    setIsDashboardOpen: (open: boolean) => void;
    sceneViewer: any;
    handleSelectLayoutCard: (key: string, details: AllAiCombinationsLayoutCardDetails) => void;
    handlePricingDollarClick: (key: string, details: AllAiCombinationsLayoutCardDetails) => void;
    onPricingCallClick?: (key: string) => void;
    handleWishlistVariantDesign: (
        categoryId: string,
        variant: {
            id?: string;
            label?: string;
            roofIds?: string[];
            productionW?: number;
            modules?: number;
            snapshotUrl?: string;
        },
    ) => void;
    solarUtilityData: unknown;
    ensureMainCardPricingForCard: (
        cardKey: string,
        estProd: number | undefined,
        systemKw: string,
    ) => Promise<unknown | null>;
    getDesignEpcByPartner: (
        cardKey: string,
        estProd?: number,
        systemKw?: string,
    ) => Record<EpcDisplayPartner, DesignEpcCell>;
    getMainCardPartnerCellState: (
        cardKey: string,
        partner: EpcDisplayPartner,
        estProd?: number,
        systemKw?: string,
    ) => { status?: string; errorDetail?: string } | undefined;
    isMainCardEpcLoading: (cardKey: string, estProd?: number, systemKw?: string) => boolean;
};

export const DashboardAltLayoutOptionsModal: React.FC<DashboardAltLayoutOptionsModalProps> = ({
    theme,
    embedded,
    slideRail = false,
    open,
    onDismiss,
    closeModal,
    altLayoutsCategory,
    altLayoutsLoading,
    designCountByCategory,
    aiCategoryLayoutVariants,
    globalLayoutVariants,
    roofCombinationsFromAi,
    layoutProductionByKey,
    layoutModulesByKey,
    layoutMetricsByKey,
    activeModule,
    dashboardSystemSizeKw,
    savedAIDesigns,
    selectedDesignForPricing,
    pricingDesignForTrigger,
    pricingSelectionLoading,
    aiLayoutVariantSnapshots: _aiLayoutVariantSnapshotsProp,
    layoutSnapshotsByKey: _layoutSnapshotsByKeyProp,
    selectedRoofCombinationKey,
    annualUsageCapKwh,
    isLoadingSnapshots,
    handleUseThisLayout,
    setIsDashboardOpen,
    sceneViewer,
    handleSelectLayoutCard,
    handlePricingDollarClick,
    onPricingCallClick,
    handleWishlistVariantDesign,
    solarUtilityData,
    ensureMainCardPricingForCard,
    getDesignEpcByPartner,
    getMainCardPartnerCellState,
    isMainCardEpcLoading,
}) => {
    const [epcModalOpen, setEpcModalOpen] = useState(false);
    const handleOpenEpcModal = useCallback(() => setEpcModalOpen(true), []);
    const handleCloseEpcModal = useCallback(() => setEpcModalOpen(false), []);
    const aiDesignsDataSource = useViewerStore(
        (s: any) => (s.aiDesignsDataSource as AiDesignsDataSource | undefined) ?? null,
    );
    const categoryMainLayoutByCategory = useViewerStore(
        (s: any) => (s.categoryMainLayoutByCategory ?? {}) as Record<string, import('../utils/categoryBestVariantScoring').CategoryLayoutCandidate>,
    );
    const layoutModulesLayoutByKey = useViewerStore(
        (s: any) => (s.layoutModulesLayoutByKey ?? {}) as Record<string, import('../../../../../@types/types').SavedRoofModulesLayout | undefined>,
    );
    const storeCategoryLayoutVariants = useViewerStore(
        (s: any) => (s.aiCategoryLayoutVariants ?? {}) as Record<string, unknown[]>,
    );
    const layoutSnapshotsByKey = useViewerStore(
        (s: any) => (s.layoutSnapshotsByKey ?? {}) as Record<string, string | undefined>,
    );
    const aiLayoutVariantSnapshots = useViewerStore(
        (s: any) => (s.aiLayoutVariantSnapshots ?? {}) as Record<string, string>,
    );
    const layoutSnapshotHydrationSeq = useViewerStore(
        (s: any) => Number((s as any).layoutSnapshotHydrationSeq ?? 0),
    );

    const resolveVariantThumb = useCallback(
        (variant: {
            label?: string;
            roofIds?: string[];
            dir?: unknown;
            snapshotUrl?: string;
            productionW?: number;
            modules?: number;
            snapshotCaptureError?: string;
        }) =>
            catalogCardSnapshotForDisplay(
                resolveAltLayoutVariantSnapshot({
                    variant,
                    aiLayoutVariantSnapshots,
                    layoutSnapshotsByKey,
                    selectedRoofCombinationKey,
                    aiDesignsDataSource,
                }),
                isLoadingSnapshots,
            ),
        [
            aiLayoutVariantSnapshots,
            layoutSnapshotsByKey,
            selectedRoofCombinationKey,
            aiDesignsDataSource,
            isLoadingSnapshots,
            layoutSnapshotHydrationSeq,
        ],
    );

    const innerBody = (
            <div
                className={`editor-ai-scenarios-block obd-theme-${theme} editor-ai-alt-layouts-modal__inner${embedded ? ' editor-ai-alt-layouts-modal__inner--embedded' : ''}${slideRail ? ' editor-ai-alt-layouts-modal__inner--slide-rail' : ''}`}
                style={{ overflow: 'visible', padding: 0 }}
            >
                <div className="editor-ai-layout-card-scroll-body">
                    <div className="editor-ai-designs-grid editor-ai-layout-options-stack editor-ai-designs-grid--catalog">
                {(() => {
                    const variantsByCategory =
                        Object.keys(storeCategoryLayoutVariants).length > 0
                            ? storeCategoryLayoutVariants
                            : aiCategoryLayoutVariants;
                    const rawVariants = altLayoutsCategory
                        ? resolveVariantsForCategory(altLayoutsCategory, variantsByCategory)
                        : ((globalLayoutVariants as any[] | undefined) ?? []);

                    if (altLayoutsLoading) {
                        return (
                            <div style={{ padding: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                                    <Spin tip="Generating layout options…" />
                                </div>
                                <div className="editor-ai-designs-cards editor-ai-designs-cards--catalog-masonry">
                                    {Array.from({ length: 4 }).map((_, idx) => (
                                        <div
                                            key={idx}
                                            className="editor-ai-design-card"
                                            style={{
                                                padding: 16,
                                                borderRadius: 12,
                                                border: theme === 'dark' ? '1px solid rgba(30,64,175,0.6)' : '1px solid #e2e8f0',
                                                background:
                                                    theme === 'dark'
                                                        ? 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,64,175,0.7))'
                                                        : 'linear-gradient(135deg, #f8fafc, #e0f2fe)',
                                            }}
                                        >
                                            <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 3 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    if (!rawVariants || rawVariants.length === 0) {
                        return (
                            <div style={{ padding: 24, textAlign: 'center' }}>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: 13,
                                        color: theme === 'dark' ? 'rgba(203,213,225,0.9)' : '#64748b',
                                    }}
                                >
                                    No alternate layout options are available for this category yet.
                                </p>
                            </div>
                        );
                    }
                    const ignoreSolarAccessForOneDrawDefaultMaxfill =
                        altLayoutsCategory != null &&
                        altLayoutsCategory.trim().toLowerCase() === 'onedraw default maxfill';
                    const displayVariants = filterAltLayoutDisplayVariants({
                        category: altLayoutsCategory,
                        variants: rawVariants,
                        categoryMainLayoutByCategory,
                        aiDesignsDataSource,
                        ignoreSolarAccessForOneDrawDefaultMaxfill,
                        layoutModulesLayoutByKey,
                        layoutProductionByKey,
                        layoutModulesByKey,
                        layoutMetricsByKey,
                    });
                    const removedVariants = rawVariants.filter((variant: AltLayoutVariantRow) => {
                        const resolved = resolveAltLayoutVariantMetrics({
                            category: altLayoutsCategory,
                            variant,
                            layoutModulesLayoutByKey,
                            layoutProductionByKey,
                            layoutModulesByKey,
                            layoutMetricsByKey,
                        });
                        const modulesOk = positiveLayoutMetric(resolved.modules) != null;
                        const productionOk = positiveLayoutMetric(resolved.productionW) != null;
                        return !modulesOk && !productionOk;
                    });
                    const modulePowerVariantW = (activeModule as any)?.power ?? 0;
                    const altLayoutEpcRows: DashboardAiDesignEpcRow[] = displayVariants
                        .map((v, idx) => {
                            const estProd = positiveLayoutMetric(v.productionW);
                            const estModules = positiveLayoutMetric(v.modules);
                            const systemKwDisplay =
                                estModules != null && modulePowerVariantW > 0
                                    ? ((estModules * modulePowerVariantW) / 1000).toFixed(2)
                                    : estProd != null
                                      ? (estProd / 1000).toFixed(2)
                                      : '0';
                            const cardKeyModal = `${altLayoutsCategory || 'other'}:${v.id ?? idx}`;
                            const rawLabel = String((v.label as string | undefined) ?? '').trim();
                            const label =
                                altLayoutsCategory?.trim().toLowerCase() === 'onedraw default maxfill' &&
                                rawLabel.toLowerCase().includes('mixed')
                                    ? 'Fill both (mixed)'
                                    : rawLabel || `Layout option ${idx + 1}`;
                            if (estProd == null && (estModules == null || modulePowerVariantW <= 0)) return null;
                            if (!Number.isFinite(Number(systemKwDisplay)) || Number(systemKwDisplay) <= 0) return null;
                            const estProdForRow =
                                estProd ??
                                (estModules != null && modulePowerVariantW > 0
                                    ? estModules * modulePowerVariantW
                                    : null);
                            if (estProdForRow == null) return null;
                            return {
                                cardKey: cardKeyModal,
                                label,
                                snapshotUrl: resolveVariantThumb(v).url,
                                estProd: estProdForRow,
                                systemKw: systemKwDisplay,
                                multiplier:
                                    positiveLayoutMetric(v.multiplier) ?? null,
                            };
                        })
                        .filter((row): row is NonNullable<typeof row> => row != null);
                    return (
                        <>
                            <div
                                role="list"
                                className={`obd-ai-catalog-list obd-theme-${theme}${
                                    slideRail
                                        ? ' obd-ai-catalog-list--slide-rail'
                                        : ' obd-ai-catalog-list--dashboard obd-ai-catalog-list--alt-layouts'
                                }`}
                            >
                                {displayVariants.length === 0 ? (
                                    <p
                                        className="obd-ai-slide-alt-layouts__empty"
                                        style={{
                                            margin: '8px 2px 4px',
                                            fontSize: 13,
                                            lineHeight: 1.45,
                                            color: theme === 'dark' ? 'rgba(156,163,175,0.95)' : 'rgba(71,85,105,0.95)',
                                        }}
                                    >
                                        No additional layout options to show — variants either match the main
                                        layouts above or had no modules on this site.
                                    </p>
                                ) : null}
                                {displayVariants.map((v, idx) => {
                                    const estProd = positiveLayoutMetric(v.productionW);
                                    const estModules = positiveLayoutMetric(v.modules);
                                    const systemKw =
                                        estProd != null ? (estProd / 1000).toFixed(2) : '—';
                                    const effLabel =
                                        positiveLayoutMetric(v.efficiency) != null
                                            ? `${Math.round(v.efficiency as number)} %`
                                            : '—';
                                    const solarLabel =
                                        positiveLayoutMetric(v.solarAccess) != null
                                            ? `${(v.solarAccess as number).toFixed(1)}%`
                                            : '—';
                                    const multiplierLabel =
                                        positiveLayoutMetric(v.multiplier) != null
                                            ? `×${Number(v.multiplier).toFixed(4)}`
                                            : '—';
                                    const rawLabel = String((v.label as string | undefined) ?? '').trim();
                                    const resolvedTitle =
                                        altLayoutsCategory?.trim().toLowerCase() === 'onedraw default maxfill' &&
                                        rawLabel.toLowerCase().includes('mixed')
                                            ? 'Fill both (mixed)'
                                            : rawLabel;
                                    const label = resolvedTitle || `Layout option ${idx + 1}`;
                                    const roofCount =
                                        typeof v.roofCount === 'number' && Number.isFinite(v.roofCount) && v.roofCount > 0
                                            ? v.roofCount
                                            : Array.isArray(v.roofIds)
                                              ? v.roofIds.length
                                              : '—';
                                    const cardKeyModal = `${altLayoutsCategory || 'other'}:${v.id ?? idx}`;
                                    const isSelected = selectedDesignForPricing?.designId === cardKeyModal;
                                    const variantWishlistCategory = altLayoutsCategory || 'Other layout options';
                                    const variantWishlistLayoutKey = `variant:${variantWishlistCategory}:${String(v.id ?? label ?? idx)}`;
                                    const isVariantWishlisted = savedAIDesigns.some(
                                        (d) =>
                                            d.layoutKey === variantWishlistLayoutKey && d.categoryId === variantWishlistCategory,
                                    );
                                    const thumbSnapshotResult = resolveVariantThumb(v);
                                    const thumbSnapshot = thumbSnapshotResult.url;
                                    const thumbSnapshotError = thumbSnapshotResult.error;
                                    const modulePowerVariantW = (activeModule as any)?.power ?? 0;
                                    const variantDisqualified = isLayoutProductionOverAnnualUsage(
                                        estProd ??
                                            (estModules != null && modulePowerVariantW > 0
                                                ? estModules * modulePowerVariantW
                                                : undefined),
                                        annualUsageCapKwh,
                                    );
                                    const systemKwDisplay =
                                        estModules != null && modulePowerVariantW > 0
                                            ? ((estModules * modulePowerVariantW) / 1000).toFixed(2)
                                            : systemKw;
                                    const prodLineDisplay =
                                        estProd != null
                                            ? `${Math.round(estProd).toLocaleString()} W`
                                            : estModules != null && modulePowerVariantW > 0
                                              ? `${Math.round(estModules * modulePowerVariantW).toLocaleString()} W`
                                              : '—';
                                    const detailLineCatalog =
                                        `Solar Access: ${solarLabel}\n` +
                                        `Efficiency: ${effLabel}\n` +
                                        `Multiplier: ${multiplierLabel}`;

                                    const applyVariant = async () => {
                                        const scenarioIndex =
                                            typeof v.scenarioIndex === 'number' && Number.isFinite(v.scenarioIndex)
                                                ? v.scenarioIndex
                                                : null;
                                        const isGlobalVariantList = altLayoutsCategory == null;
                                        if (
                                            isGlobalVariantList &&
                                            scenarioIndex != null &&
                                            typeof handleUseThisLayout === 'function'
                                        ) {
                                            handleUseThisLayout(scenarioIndex);
                                            closeModal();
                                            setIsDashboardOpen(false);
                                            return;
                                        }
                                        const self = sceneViewer;
                                        const roofIds = (v.roofIds as string[]) || [];
                                        if (!self || roofIds.length === 0) return;
                                        try {
                                            await restoreInitialDefaultLayout(self);
                                        } catch {
                                            // if restoring fails, still try to apply variant
                                        }
                                        setIsDashboardOpen(false);
                                        addModulesToRoofsByIds(self, roofIds, v.dir ?? null);
                                        await SetModuleGraph(self);
                                        removeGreyModulesAndSingleModuleRoofs(self, roofIds, v.minEff ?? undefined);
                                        await SetModuleGraph(self);
                                        closeModal();
                                    };

                                    const openVariantIn3d = async () => {
                                        const self = sceneViewer;
                                        const roofIds = (v.roofIds as string[]) || [];
                                        if (!self || roofIds.length === 0) return;
                                        setIsDashboardOpen(false);
                                        addModulesToRoofsByIds(self, roofIds, v.dir ?? null);
                                        await SetModuleGraph(self);
                                        removeGreyModulesAndSingleModuleRoofs(self, roofIds, v.minEff ?? undefined);
                                        await SetModuleGraph(self);
                                        closeModal();
                                    };

                                    const resolvedEstProd =
                                        estProd ??
                                        (estModules != null && modulePowerVariantW > 0
                                            ? estModules * modulePowerVariantW
                                            : undefined);

                                    const selectPayload = {
                                        estProd: resolvedEstProd,
                                        estModules,
                                        systemKw: systemKwDisplay,
                                        roofCount: typeof roofCount === 'number' ? roofCount : 0,
                                        categoryId: altLayoutsCategory || 'Other layout options',
                                        designId: cardKeyModal,
                                    };
                                    const pricingBusy =
                                        pricingSelectionLoading &&
                                        (isSelected || pricingDesignForTrigger?.designId === cardKeyModal);
                                    const prodLineForRow = slideRail
                                        ? resolvedEstProd != null
                                            ? `${Math.round(resolvedEstProd).toLocaleString()} kWh`
                                            : '—'
                                        : prodLineDisplay;

                                    return (
                                        <DashboardAiCatalogListRow
                                            key={v.id ?? idx}
                                            theme={theme}
                                            rowIndex={idx}
                                            cardKey={String(v.id ?? idx)}
                                            snapshotUrl={thumbSnapshot}
                                            snapshotError={thumbSnapshotError}
                                            snapshotLoading={isLoadingSnapshots && !thumbSnapshot}
                                            emptyThumb="sparkle"
                                            showThumbIndex={slideRail ? false : undefined}
                                            showNotSellableStamp={variantDisqualified}
                                            notSellableReason={
                                                variantDisqualified
                                                    ? formatLayoutNotSellableReason(annualUsageCapKwh)
                                                    : undefined
                                            }
                                            label={label}
                                            summaryLine={`${estModules ?? '—'} modules · ${roofCount} roofs`}
                                            prodLine={prodLineForRow}
                                            detailLine={slideRail ? undefined : detailLineCatalog}
                                            statusLabel={
                                                variantDisqualified ? 'Below usage' : isSelected ? 'Active' : 'Ready'
                                            }
                                            statusVariant={
                                                variantDisqualified ? 'muted' : isSelected ? 'lime' : 'neutral'
                                            }
                                            systemKw={systemKwDisplay}
                                            isSelected={isSelected}
                                            isSimulating={false}
                                            disqualified={variantDisqualified}
                                            selectForPricingDisabled={
                                                slideRail
                                                    ? variantDisqualified ||
                                                      (pricingSelectionLoading &&
                                                          (isSelected ||
                                                              pricingDesignForTrigger?.designId === cardKeyModal))
                                                    : false
                                            }
                                            pricingBusy={pricingBusy}
                                            pricingDisabled={variantDisqualified || pricingBusy}
                                            onSelectForPricing={() => {
                                                if (slideRail) {
                                                    handleSelectLayoutCard('default', selectPayload);
                                                    return;
                                                }
                                                void applyVariant();
                                            }}
                                            onActivateDesign={
                                                slideRail
                                                    ? () => {
                                                          if (variantDisqualified) return;
                                                          if (!isSelected) {
                                                              handleSelectLayoutCard('default', selectPayload);
                                                          }
                                                          void openVariantIn3d();
                                                      }
                                                    : undefined
                                            }
                                            onPricing={() => {
                                                handlePricingDollarClick('default', selectPayload);
                                                onPricingCallClick?.('default');
                                                if (!slideRail) closeModal();
                                            }}
                                            onOpen3D={() => {
                                                void openVariantIn3d();
                                            }}
                                            variant={slideRail ? 'rail' : 'dashboard'}
                                            showEpcAction={
                                                slideRail ? false : !variantDisqualified && Boolean(solarUtilityData)
                                            }
                                            epcBusy={slideRail ? false : isMainCardEpcLoading(cardKeyModal)}
                                            onOpenEpc={slideRail ? undefined : handleOpenEpcModal}
                                            extraActions={
                                                slideRail ? undefined : (
                                                    <>
                                                    <button
                                                        type="button"
                                                        className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn${
                                                            isSelected ? ' obd-ai-catalog-list__icon-btn--selected' : ''
                                                        }`}
                                                        title={
                                                            variantDisqualified
                                                                ? 'Not sellable — production exceeds annual usage'
                                                                : 'Select for pricing'
                                                        }
                                                        aria-label="Select for pricing"
                                                        disabled={
                                                            variantDisqualified || (pricingSelectionLoading && isSelected)
                                                        }
                                                        onClick={() => {
                                                            handleSelectLayoutCard('default', {
                                                                estProd,
                                                                estModules,
                                                                systemKw,
                                                                roofCount: typeof roofCount === 'number' ? roofCount : 0,
                                                                categoryId: altLayoutsCategory || 'Other layout options',
                                                                designId: cardKeyModal,
                                                            });
                                                        }}
                                                    >
                                                        {pricingSelectionLoading && isSelected ? (
                                                            <Spin size="small" style={{ display: 'block' }} />
                                                        ) : (
                                                            <CheckSquareIcon
                                                                size={16}
                                                                weight={isSelected ? 'fill' : 'duotone'}
                                                                aria-hidden
                                                            />
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`obd-ai-slide-designs__3d-btn obd-ai-catalog-list__icon-btn${
                                                            isVariantWishlisted ? ' obd-ai-catalog-list__icon-btn--saved' : ''
                                                        }`}
                                                        title={isVariantWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                                        aria-label={
                                                            isVariantWishlisted ? 'Remove from wishlist' : 'Add to wishlist'
                                                        }
                                                        onClick={() => {
                                                            handleWishlistVariantDesign(variantWishlistCategory, {
                                                                id: String(v.id ?? idx),
                                                                label,
                                                                roofIds: (v.roofIds as string[]) || [],
                                                                productionW: estProd,
                                                                modules: estModules,
                                                                snapshotUrl: thumbSnapshot,
                                                            });
                                                        }}
                                                    >
                                                        <HeartIcon
                                                            size={16}
                                                            weight={isVariantWishlisted ? 'fill' : 'duotone'}
                                                            aria-hidden
                                                        />
                                                    </button>
                                                </>
                                                )
                                            }
                                        />
                                    );
                                })}
                            </div>
                            {removedVariants.length > 0 && !slideRail && (
                                <details
                                    style={{
                                        marginTop: 8,
                                        padding: '6px 10px',
                                        borderRadius: 8,
                                        background: theme === 'dark' ? 'rgba(15,23,42,0.55)' : '#f3f4f6',
                                        border: theme === 'dark' ? '1px solid rgba(148,163,184,0.35)' : '1px solid #e5e7eb',
                                    }}
                                >
                                    <summary
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            color: theme === 'dark' ? 'rgba(226,232,240,0.9)' : '#374151',
                                        }}
                                    >
                                        Additional info
                                    </summary>
                                    <div style={{ marginTop: 6 }}>
                                        <p
                                            style={{
                                                fontSize: 12,
                                                color: theme === 'dark' ? 'rgba(148,163,184,0.85)' : '#4b5563',
                                                marginBottom: 0,
                                            }}
                                        >
                                            Gemini tried additional layout variations for this category that ended up with no
                                            modules because they did not fulfill the case requirements. Those empty designs were
                                            hidden from the grid above:
                                        </p>
                                        <ul style={{ margin: '4px 0 0', paddingLeft: 16, fontSize: 12 }}>
                                            {removedVariants.map((v, ridx) => (
                                                <li key={v.id ?? ridx}>
                                                    {String((v as any).label ?? `Variation ${ridx + 1}`)}
                                                </li>
                                            ))}
                                        </ul>
                                        {displayVariants.length === 0 && (
                                            <p
                                                style={{
                                                    fontSize: 12,
                                                    color: theme === 'dark' ? 'rgba(148,163,184,0.75)' : '#6b7280',
                                                    marginTop: 4,
                                                }}
                                            >
                                                No non-empty layouts remained for this category.
                                            </p>
                                        )}
                                    </div>
                                </details>
                            )}
                            {!slideRail ? (
                                <DashboardAiDesignsEpcModal
                                    theme={theme}
                                    open={epcModalOpen}
                                    onClose={handleCloseEpcModal}
                                    designs={altLayoutEpcRows}
                                    ensurePricingForCard={ensureMainCardPricingForCard}
                                    getEpcByPartner={getDesignEpcByPartner}
                                    getPartnerCellState={getMainCardPartnerCellState}
                                    selectedCardKey={selectedDesignForPricing?.designId ?? null}
                                    annualUsageCapKwh={annualUsageCapKwh}
                                    zIndex={embedded ? undefined : OBD_AI_DESIGNS_EPC_NESTED_MODAL_Z}
                                />
                            ) : null}
                        </>
                    );
                })()}
                    </div>
                </div>
            </div>
    );

    if (embedded) {
        return innerBody;
    }

    return (
        <DashboardModal
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <SquaresFourIcon size={20} weight="duotone" />{' '}
                    {altLayoutsCategory ? `${altLayoutsCategory} – other options` : 'Other layout options'}
                    {altLayoutsCategory != null && (
                        <span style={{ fontWeight: 'normal', opacity: 0.85, fontSize: '0.9em' }}>
                            {' '}({designCountByCategory[altLayoutsCategory] ?? 0} main designs)
                        </span>
                    )}
                </span>
            }
            open={open}
            onClose={onDismiss}
            theme={theme === 'light' ? 'light' : 'dark'}
            footer={null}
            className={`editor-ai-tree-cutting-modal editor-ai-alt-layouts-modal obd-theme-${theme}`}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
            zIndex={OBD_DASHBOARD_MODAL_Z}
            destroyOnClose
        >
            {innerBody}
        </DashboardModal>
    );
};
